<template>
  <Main>
    <BackButton text="Back to Project" :route="$routes.project(projectId)" />
    <v-container style="max-width: 1366px;" v-if="pythonErrors">
      <v-alert type="error" icon="fas fa-warning" variant="tonal">
        <p>
          There were problems during the analysis and it could not be performed. If you think that this is a mistake
          please contact the developers.
        </p>
        <br>
        <b>Error Message:</b>
        {{ pythonErrors.errors }}
      </v-alert>
    </v-container>
    <v-container style="max-width: 1366px;" v-else>
      <h2 class="text-center mb-15">SII & SPI Analysis</h2>
      <DividerWithText text="Graphical Representation" color="#abc" text-color="#58a" />
      <v-row class="">
        <v-col cols="6 mb-3">
          <v-card class="pa-3 mt-3 elevation-1">
            <!-- SII + AGG. LEVELS -->
            <SimpleVerticalBarChart
              v-if="finalSiiChartData"
              :chartData="finalSiiChartData"
              :axisLabels="{ x: 'Aggregation Level', y: 'Score' }"
              :showColorCode="false"
              :aspectRatio="{ width: 2, height: 1 }"
              title="Sustainability Impact Index (SII)"
              id="graph1"
            >
            </SimpleVerticalBarChart>
          </v-card>
        </v-col>

        <!-- SPI + AGG. LEVELS -->
        <v-col cols="6 mb-3">
          <v-card class="pa-3 mt-3 elevation-1">
            <SimpleVerticalBarChart
              v-if="finalSpiChartData"
              :chartData="finalSpiChartData"
              :axisLabels="{ x: 'Aggregation Level', y: 'Score' }"
              :showColorCode="false"
              :aspectRatio="{ width: 2, height: 1 }"
              title="Sustainability Performance Index (SPI)"
              id="graph2"
            >
            </SimpleVerticalBarChart>
          </v-card>
        </v-col>

        <v-col cols="6 mb-3">
          <v-card class="pa-3 mt-3 elevation-1">
            <!-- DIMENSION SCORES PER AGG. LEVEL -->
            <RadarChartWithSelect
              title="Dimensions score"
              selectPrompt="Aggregation Level:"
              :itemsData="radarChartData"
              id="graph3"
            >
            </RadarChartWithSelect>
          </v-card>
        </v-col>

        <v-col cols="6 mb-3">
          <v-card class="pa-3 mt-3 elevation-1">
            <!-- SECTOR SCORES PER AGG. LEVEL -->
            <RadarChartWithSelect
              title="Sectors score"
              selectPrompt="Aggregation Level:"
              :itemsData="sectorsRadarChartData"
              id="graph4"
            >
            </RadarChartWithSelect>
          </v-card>
        </v-col>

        <v-col class="mb-3">
          <v-card class="pa-3 mt-3 elevation-1">
            <ChartWithSelect
              :availableItems="availableDimensions"
              :itemsData="dimensionData"
              :averageScores="averageScores"
              :showFinalSII="true"
              selectPrompt="Dimension:"
              id="graph5"
            ></ChartWithSelect>
          </v-card>
        </v-col>

        <v-col class="mb-3">
          <v-card class="pa-3 mt-3 elevation-1">
            <ChartWithSelect
              :availableItems="availableSectors"
              :itemsData="sectorsData"
              :averageScores="finalSpiScores"
              :showFinalSII="true"
              isSpiOrSii="spi"
              selectPrompt="Sector:"
              id="graph6"
            ></ChartWithSelect>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-btn @click="showOptionalRadarCharts = !showOptionalRadarCharts">Show KPI scores per dimension and sector</v-btn>
        </v-col>

        <v-col cols="6 mb-3" v-if="showOptionalRadarCharts">
          <v-card class="pa-3 mt-3 elevation-1">
            <!-- KPI SCORES PER AGG. LEVEL + DIMENSION -->
            <RadarChartWithSelect
                title="KPI scores per dimension"
                selectPrompt="Aggregation Level:"
                selectPrompt2="Dimension:"
                :itemsData="radarChartData"
                :itemsData2="kpiRadarDimensionData"
                id="graph3-kpis"
            >
            </RadarChartWithSelect>
          </v-card>
        </v-col>

        <v-col cols="6 mb-3" v-if="showOptionalRadarCharts">
          <v-card class="pa-3 mt-3 elevation-1">
            <!-- KPI SCORES PER AGG. LEVEL + SECTOR -->
            <RadarChartWithSelect
                title="KPI scores per sector"
                selectPrompt="Aggregation Level:"
                selectPrompt2="Sector:"
                :itemsData="sectorsRadarChartData"
                :itemsData2="kpiRadarSectorData"
                id="graph4-kpis"
            >
            </RadarChartWithSelect>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card class="pa-3 mt-3 elevation-1">
            <HorizontalBarChartWithSelect
              :availableItems="Object.keys(kpisData)"
              :itemsData="kpisData"
              :annotations="chartAnnotations"
              :averageScores="averageScores"
              chartType="horizontal"
              selectPrompt="KPI:"
              :axisLabels="{ x: 'Score', y: '' }"
              :key="`bar_key_${horizontalBarKey}`"
              id="graph7"
            ></HorizontalBarChartWithSelect>
          </v-card>
        </v-col>
      </v-row>

      <DividerWithText class="my-8" text="Analytic SΙI Scores" color="#666" text-color="#666" />
      <div class="d-none" id="sticky-download-button">
        <DownloadSpiAndSiiTablesButton :sii-table-data="kpiTableSiiData"
                                       :spi-table-data="kpiTableSpiData"
                                       :project-id="projectId"
                                       :project-name="projectName"
                                       :user-email="userEmail"
        />
      </div>

      <KpiAnalysisSiiTable :table-data="kpiTableSiiData" :button-data="kpiTableSpiData"
                           :project-id="projectId"
                           :project-name="projectName"
                           :user-email="userEmail"
      />
      <DividerWithText class="my-8" text="Analytic SPI Scores" color="#666" text-color="#666" />
      <KpiAnalysisSpiData :kpi-table-data="kpiTableSpiData" :button-data="kpiTableSiiData" />

    </v-container>
  </Main>
</template>

<script setup>
import Main from "../../Layouts/Main.vue";
import StringManipulation from "../../Mixins/StringManipulation";
import ChartWithSelect from "../../Charts/select/ChartWithSelect.vue";
import SimpleVerticalBarChart from "../../Charts/simple/SimpleVerticalBarChart.vue";
import RadarChartWithSelect from "../../Charts/select/RadarChartWithSelect.vue";
import HorizontalBarChartWithSelect from "../../Charts/select/HorizontalBarChartWithSelect.vue";
import { computed, onMounted, ref } from "vue";
import * as $routes from "@/packs/routes";
import BackButton from "@/Shared/BackButton.vue";
import DividerWithText from "@/Shared/DividerWithText.vue";
import KpiAnalysisSiiTable from "@/Pages/Projects/subcomponents/KpiAnalysisSiiTable.vue";
import KpiAnalysisSpiData from "@/Pages/Projects/subcomponents/KpiAnalysisSpiData.vue";
import DownloadSpiAndSiiTablesButton from "@/Pages/Projects/subcomponents/DownloadSpiAndSiiTablesButton.vue";

const props = defineProps({
  userEmail: {
    type: String,
    default: null,
    required: false,
  },
  availableDimensions: {
    type: Array,
    default: null,
    required: false,
  },
  dimensionData: {
    type: Object,
    default: null,
    required: false,
  },
  availableSectors: {
    type: Array,
    default: null,
    required: false,
  },
  sectorsData: {
    type: Object,
    default: null,
    required: false,
  },
  averageScores: {
    type: Object,
    default: null,
    required: false,
  },
  kpisData: {
    type: Object,
    required: false,
  },
  projectId: {
    type: Number,
    default: null,
    required: false,
  },
  projectName: {
    type: String,
    default: null,
    required: false,
  },
  pythonErrors: {
    type: Object,
    default: null,
    required: false,
  },
  finalSpiScores: {
    type: Object,
    required: false,
  },
  kpiTableSiiData: {
    type: Object,
    required: false,
  },
  kpiTableSpiData: {
    type: Object,
    required: false,
  },
  kpiRadarDimensionData: {
    type: Object,
    required: false,
  },
  kpiRadarSectorData: {
    type: Object,
    required: false,
  }
});

const showOptionalRadarCharts = ref(false);

const selectedRadarItem = ref(null);
const horizontalBarKey = 1;

onMounted(() => {
  const obj = this;
  window.addEventListener("resize", function () {
    if (this.horizontalBarKey === 1) this.horizontalBarKey = 0;
    else this.horizontalBarKey = 1;
  });

  window.onscroll = docxTableButtonVisibility;
});

const chartAnnotations = computed(() => {
  const result = {}
  const keys = Object.keys(props.averageScores)
  for (let key of keys) {
    result[key] = [{label: 'Average', value: props.averageScores[key]}]
  }

  return result
})

function docxTableButtonVisibility() {
  const sticky = document.getElementById('sticky-download-button');
  const downloadOriginal = document.getElementById('download-original');

  if (window.scrollY > downloadOriginal.offsetTop) {
    sticky.classList.add('sticky-download');
    sticky.classList.remove('d-none');
  }
  else {
    sticky.classList.add('d-none');
    sticky.classList.remove('sticky-download');
  }
}

const spiAndSiiChartColors = [
  "rgba(240,20,22,0.75)",
  "rgba(51,94,201,0.75)",
  "rgb(68,170,68,0.75)",
  "rgba(231,228,79,0.75)",
  "rgba(141,51,201,0.75)"
];

const finalSiiChartData = computed(() => {
  let labels = [];
  let data = [];
  const colors = []

  // TODO: This should go to server side
  let keys = ["building", "peb", "ped", "city", "island"];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = props.averageScores[key]
    if (value === null || value === undefined) {
      continue
    }

    colors.push(spiAndSiiChartColors[i])
    labels.push(StringManipulation.methods.clearAndCapitalize(key));
    data.push(value);
  }

  return {
    labels: labels,
    datasets: [
      {
        label: 'Final SII Scores',
        data: data,
        backgroundColor: colors,
      },
    ],
  };
});


const finalSpiChartData = computed(() => {
  let labels = [];
  let data = [];
  const colors = []

  // TODO: This should go to server side
  let keys = ["building", "peb", "ped", "city", "island"];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = props.finalSpiScores[key]
    if (value === null || value === undefined) {
      continue
    }

    colors.push(spiAndSiiChartColors[i])
    labels.push(StringManipulation.methods.clearAndCapitalize(key));
    data.push(value);
  }

  return {
    labels: labels,
    datasets: [
      {
        label: "Final SPI Scores",
        data: data,
        backgroundColor: colors,
      },
    ],
  };
});

const radarChartColorCoding = {
  "Building": [
    "rgba(255, 99, 132, 0.2)",
    "rgb(255, 99, 132)",
    "rgb(255, 99, 132)",
    "rgb(255, 99, 132)",
  ],

  "PEB": [
    "rgba(99,143,255,0.2)",
    "rgb(99,125,255)",
    "rgb(99,154,255)",
    "rgb(99,125,255)",
  ],

  "PED": [
    "rgba(128,255,99,0.2)",
    "rgb(104,255,99)",
    "rgb(128,255,99)",
    "rgb(115,255,99)",
  ],

  "City": [
    "rgba(255,211,99,0.2)",
    "rgb(255,234,99)",
    "rgb(255,229,99)",
    "rgb(255,229,99)",
  ],

  "Island": [
    "rgba(79,15,158,0.2)",
    "rgb(104,45,209)",
    "rgb(104,45,209)",
    "rgb(104,45,209)",
  ]
}

const radarChartData = computed(() => {
  let radarDataHash = {};
  for (let dKey in props.dimensionData) {
    const dimension = props.dimensionData[dKey];
    const labels = dimension.data.labels;
    const data = dimension.data.datasets[0].data;
    const length = data.length;

    // parallel sort arrays:
    let combined = labels.map((label, index) => [label, data[index]]);
    combined.sort((a, b) => a[1] - b[1]);
    const sortedLabels = combined.map(item => item[0]);
    const sortedData = combined.map(item => item[1]);

    for (let i = 0; i < length; i++) {
      const label = sortedLabels[i];
      // if the key does not exist
      const humanizedLabel = StringManipulation.methods.clearAndCapitalize(label);
      if (!radarDataHash[label]) {
        radarDataHash[label] = {
          labels: [],
          datasets: [
            {
              label: humanizedLabel,
              data: [],
              fill: true,
              backgroundColor: radarChartColorCoding[humanizedLabel] ? radarChartColorCoding[humanizedLabel][0] : "rgba(0,0,0,0.1)",
              borderColor: radarChartColorCoding[humanizedLabel] ? radarChartColorCoding[humanizedLabel][1] : "rgb(0,0,0)",
              pointBackgroundColor: radarChartColorCoding[humanizedLabel] ? radarChartColorCoding[humanizedLabel][2] : "rgb(0,0,0)",
              pointBorderColor: "#fff",
              pointHoverBackgroundColor: "#fff",
              pointHoverBorderColor: radarChartColorCoding[humanizedLabel] ? radarChartColorCoding[humanizedLabel][3] : "rgb(0,0,0)",
            },
          ],
        };
      }
      radarDataHash[label].labels.push(
        StringManipulation.methods.clearAndCapitalize(dKey)
      );
      radarDataHash[label].datasets[0].data.push(sortedData[i]);
    }
  }
  // return radarDataHash
  return radarDataHash;
});

// This method is used to inverse the data of the first bar graph
// Generates data per dimension for each aggregated level
// { "City": { labels: ["dimension1", "dimension2", ..., "dimensionN], data: [score1, score2, ..., scoreN] }}
const sectorsRadarChartData = computed(() => {
  let radarDataHash = {};
  for (let dKey in props.sectorsData) {
    const sector = props.sectorsData[dKey];
    const labels = sector.data.labels;
    const data = sector.data.datasets[0].data;
    const length = data.length;

    // parallel sort arrays:
    let combined = labels.map((label, index) => [label, data[index]]);
    combined.sort((a, b) => a[1] - b[1]);
    const sortedLabels = combined.map(item => item[0]);
    const sortedData = combined.map(item => item[1]);

    for (let i = 0; i < length; i++) {
      const label = sortedLabels[i];
      // if the key does not exist
      const humanizedLabel = StringManipulation.methods.clearAndCapitalize(label);
      if (!radarDataHash[label]) {
        radarDataHash[label] = {
          labels: [],
          datasets: [
            {
              label: humanizedLabel,
              data: [],
              fill: true,
              backgroundColor: radarChartColorCoding[humanizedLabel] ? radarChartColorCoding[humanizedLabel][0] : "rgba(0,0,0,0.1)",
              borderColor: radarChartColorCoding[humanizedLabel] ? radarChartColorCoding[humanizedLabel][1] : "rgb(0,0,0)",
              pointBackgroundColor: radarChartColorCoding[humanizedLabel] ? radarChartColorCoding[humanizedLabel][2] : "rgb(0,0,0)",
              pointBorderColor: "#fff",
              pointHoverBackgroundColor: "#fff",
              pointHoverBorderColor: radarChartColorCoding[humanizedLabel] ? radarChartColorCoding[humanizedLabel][3] : "rgb(0,0,0)",
            },
          ],
        };
      }
      radarDataHash[label].labels.push(
        StringManipulation.methods.clearAndCapitalize(dKey.replaceAll("_", " ")).split(' ')
      );
      radarDataHash[label].datasets[0].data.push(sortedData[i]);
    }
  }

  // return radarDataHash
  return radarDataHash;
});

function onRadarChange(value) {
  selectedRadarItem.value = value;
}
</script>

<style scoped>
.sticky-download {
  position: fixed;
  align-self: center;
  top: 10px;
  right: 140px;
  z-index: 1000;
  padding: 5px;
}

</style>
